import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { reviewApi } from '../../utils/reviewApi';

const revLabel = (r) => `${r.label}${r.state ? ` · ${r.state}` : ''}`;
const cut = (s, n = 140) => (s && s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

/** A blob: URL for a fetched PDF (never an /api/* link — Note #85), revoked when it changes. */
function usePdf(load, deps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (!load) return undefined;
    let live = true;
    let u = '';
    setUrl('');
    setError('');
    load()
      .then((blob) => {
        if (!live) return;
        u = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setUrl(u);
      })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; if (u) URL.revokeObjectURL(u); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [url, error];
}

function PdfPane({ url, error, page, title, label }) {
  return (
    <div className="drv-cmp-pane">
      {label && <div className="drv-cmp-pane-label">{label}</div>}
      {error ? <div className="drv-error" role="alert" style={{ margin: 16 }}>{error}</div>
        : url ? <iframe className="drv-pdf" title={title} src={`${url}#page=${page || 1}&view=FitH`} key={`${url}-${page}`} />
          : <div className="drv-muted" style={{ padding: 24 }}>Drawing the changes on the PDF…</div>}
    </div>
  );
}

/**
 * Compare the round's draft with an earlier revision — like Word's track changes, on the
 * ORIGINAL PDF: the server reads both revisions' words with their positions, diffs them and
 * draws the result onto the real pages (tables, layout and pictures intact):
 *
 *   Track changes → the newer PDF; added/changed words highlighted green, removed text in red
 *                   balloons in a margin on the right
 *   Side by side  → the older PDF (removals struck through in red) beside the newer one
 *
 * The change list jumps to the page; the AI summary is the Orcanos QMS "Analyze with AI".
 */
export default function CompareView({ reviewId, round }) {
  const [revs, setRevs] = useState(null);
  const [against, setAgainst] = useState('');
  const [diff, setDiff] = useState(null);
  const [mode, setMode] = useState('track');
  const [page, setPage] = useState({ n: 1, o: 1 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  useEffect(() => {
    let live = true;
    setRevs(null);
    setDiff(null);
    setError('');
    reviewApi.revisions(reviewId, round)
      .then((d) => { if (live) { setRevs(d); setAgainst(d.default || ''); } })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [reviewId, round]);

  useEffect(() => {
    if (!against) return undefined;
    let live = true;
    setBusy(true);
    setDiff(null);
    setSummary(null);
    setAiError('');
    setError('');
    setPage({ n: 1, o: 1 });
    reviewApi.compare(reviewId, against, round)
      .then((d) => { if (live) { setDiff(d); setSummary(d.summary || null); } })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [reviewId, round, against]);

  const ready = !!diff;
  const [newUrl, newErr] = usePdf(ready ? () => reviewApi.comparePdfBlob(reviewId, against, 'new', round) : null,
    [ready, reviewId, against, round]);
  const [oldUrl, oldErr] = usePdf(ready && mode === 'side' ? () => reviewApi.comparePdfBlob(reviewId, against, 'old', round) : null,
    [ready, mode === 'side', reviewId, against, round]);

  const summarise = async (force) => {
    setAiBusy(true);
    setAiError('');
    try {
      const d = await reviewApi.compareSummary(reviewId, against, round, force);
      setSummary(d.summary);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  const s = diff?.stats || {};
  const n = diff?.changes?.length || 0;

  return (
    <div className="drv-cmp">
      <div className="drv-diff-toolbar">
        <span className="drv-diff-title">
          {diff ? <>Comparing <strong>{diff.from.label}</strong> → <strong>{diff.to.label}</strong></>
            : revs?.current ? <>Compare <strong>{revs.current.label}</strong> with</> : 'Compare revisions'}
        </span>
        <span className="drv-diff-toolbar-actions">
          {revs && revs.revisions.length > 0 && (
            <select className="drv-select drv-diff-select" value={against} aria-label="Compare with revision"
              onChange={(e) => setAgainst(e.target.value)}>
              {revs.revisions.map((r) => (
                <option key={r.attachment_id} value={r.attachment_id}>{revLabel(r)}</option>
              ))}
            </select>
          )}
          {diff && (
            <div className="drv-seg drv-seg--dark" role="tablist" aria-label="Layout">
              {[['track', 'Track changes'], ['side', 'Side by side']].map(([k, label]) => (
                <button type="button" role="tab" key={k} aria-selected={mode === k}
                  className={`drv-seg-btn${mode === k ? ' drv-seg-btn--on' : ''}`} onClick={() => setMode(k)}>{label}</button>
              ))}
            </div>
          )}
        </span>
      </div>

      {error && <div className="drv-error" role="alert" style={{ margin: 16 }}>{error}</div>}
      {revs && revs.revisions.length === 0 && (
        <div className="drv-note" style={{ margin: 16 }}>This document has no earlier revision to compare with.</div>
      )}
      {busy && <div className="drv-muted drv-cmp-wait">Reading both revisions as PDF from Orcanos and comparing them…</div>}

      {diff && (
        <div className="drv-cmp-body">
          <aside className="drv-cmp-side" aria-label="Changes">
            <div className="drv-cmp-ai">
              <div className="drv-cmp-ai-head">
                <strong>AI summary</strong>
                <button type="button" className="drv-btn" disabled={aiBusy || (n === 0 && !!summary)}
                  onClick={() => summarise(!!summary)}>
                  {aiBusy ? 'Summarising…' : summary ? '✨ Summarise again' : '✨ Summarise with AI'}
                </button>
              </div>
              {aiError && <div className="drv-error" role="alert">{aiError}</div>}
              {summary ? (
                <div className="drv-cmp-ai-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary.text}</ReactMarkdown>
                  <div className="drv-small">{summary.by_name ? `By ${summary.by_name} · ` : ''}AI-generated — check it against the changes.</div>
                </div>
              ) : !aiBusy && (
                <div className="drv-small">What changed, what it likely affects, and how big the impact is.</div>
              )}
            </div>

            <div className="drv-cmp-count">
              {n === 0 ? 'No differences in the text.'
                : `${n} change${n === 1 ? '' : 's'}`
                  + ` — ${[s.replaced && `${s.replaced} changed`, s.inserted && `${s.inserted} added`, s.deleted && `${s.deleted} removed`].filter(Boolean).join(', ')}`}
            </div>
            {diff.empty_side && (
              <div className="drv-note">
                The {diff.empty_side === 'old' ? diff.from.label : diff.to.label} PDF has no readable text — it may be a scanned image.
              </div>
            )}
            <ol className="drv-cmp-list">
              {diff.changes.map((c) => (
                <li key={c.n}>
                  <button type="button" className="drv-cmp-item" onClick={() => setPage({ n: c.page, o: c.old_page || c.page })}
                    title={`Go to page ${c.page}`}>
                    <span className="drv-cmp-page">p.{c.page}</span>
                    <span className="drv-cmp-what">
                      {c.old && <del className="drv-diff-del">{cut(c.old)}</del>}
                      {c.old && c.new && ' '}
                      {c.new && <ins className="drv-diff-ins">{cut(c.new)}</ins>}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>

          <div className={`drv-cmp-pdfs${mode === 'side' ? ' drv-cmp-pdfs--side' : ''}`}>
            {mode === 'side' && (
              <PdfPane url={oldUrl} error={oldErr} page={page.o} title={diff.from.label}
                label={<>{diff.from.label} — <del className="drv-diff-del">removed</del></>} />
            )}
            <PdfPane url={newUrl} error={newErr} page={page.n} title={diff.to.label}
              label={mode === 'side' ? <>{diff.to.label} — <ins className="drv-diff-ins">added</ins></> : null} />
          </div>
        </div>
      )}
    </div>
  );
}

/** The draft as Orcanos' own PDF rendition, in the browser's viewer (a blob: URL, Note #85). */
export function PdfRenditionView({ reviewId, round }) {
  const [url, error] = usePdf(() => reviewApi.pdfBlob(reviewId, round), [reviewId, round]);
  if (error) return <div className="drv-error" role="alert" style={{ margin: 24 }}>{error}</div>;
  if (!url) return <div className="drv-muted" style={{ padding: 32 }}>Opening the PDF from Orcanos…</div>;
  return <iframe className="drv-pdf" title="The draft as PDF" src={`${url}#toolbar=1`} />;
}
